import { inject, Injectable, NgZone } from '@angular/core';
import { AppDB, OBEGE, OBEGP, SXBEP } from './dexieDB';
import { AsMappedRows, AsMappedTableFE, AsTableFE, GetDBStatus, SharedDB } from './sharedDB';
import { FileForPreview } from 'src/app/content/_components/_shared/preview-thumbnails/preview-thumbnail.type';
import { MRproResponseInfo } from 'src/app/types/api';
import { BegehungHistorieNode, BegehungPlanNode, BegehungResult } from 'src/app/content/begehung/begehung.component';
import dayjs from 'dayjs';
import { BackendLocaleDatePipe } from 'src/app/pipes/get-locale-date.pipe';
import Dexie from 'dexie';

@Injectable({ providedIn: 'root' })
export class BegehungDB {
  private readonly zone = inject(NgZone);

  private readonly db = inject(AppDB);

  private readonly sharedDB = inject(SharedDB);
  //#region begehung
  public async setBegehung(req: any) {
    const status: string = req.status.toString();
    const obegeid: number = parseInt(req.obegeid);
    let obegpid: number = parseInt(req.obegpid);

    const listItems = [];

    if (status.toLowerCase() == 'delsxbep') {
      const ostammid = parseInt(req.ostammid);
      await this.db.sxbep.where({ OBEGP: obegpid, OSTAMMID: ostammid }).delete();

      await this.db.obege.update(obegeid, {
        LPCompleted: false,
        ...GetDBStatus(),
      });

      await this.db.obegp.update(obegpid, {
        Completed: false,
        ...GetDBStatus(),
      });

      return { success: true };
    }

    if (status.toLowerCase() == 'setsxbepcompleted') {
      await this.db.obege.update(obegeid, {
        LPCompleted: req.completed,
        ...GetDBStatus(),
      });

      await this.db.obegp.update(obegpid, {
        Completed: req.completed,
        ...GetDBStatus(),
      });

      return { success: true };
    }

    if (status.toLowerCase() == 'setsxbep') {
      const ostammid = parseInt(req.ostammid);
      const lpid = parseInt(req.lpid);

      const mediaStr = await this.sharedDB.SaveMedia('Passbilder', req.bilder, null, `${ostammid}`);
      const { UserName, Status: CompletedDate } = GetDBStatus();
      await this.db.sxbep.add({
        OBEGP: obegpid,
        OSTAMMID: ostammid,
        Completed: true,
        MangelID: await this.GetMangelItems(lpid),
        CompletedDate,
        UserName,
        PBild: mediaStr,
      });

      const body = req.vals;

      await this.db.obege.update(obegeid, {
        LPOBEGPID: obegpid,
        LPDatum: body.inspektiondate,
        ...GetDBStatus(),
      });

      return { success: true };
    }

    if (status.toLowerCase() == 'getsxbep') {
      return await this.zone.runOutsideAngular(async () => {
        type Row_Bilder = SXBEP & { bilder?: FileForPreview[] };

        const rows: Row_Bilder[] = await this.db.sxbep.where({ OBEGP: obegpid }).toArray();
        await Promise.all(
          rows.map(async row => {
            if (row.PBild) row.bilder = await this.sharedDB.GetMediaByNames(row.PBild.split('|'));
          })
        );

        const sxbep = AsTableFE(rows);
        return { success: true, result: { sxbep } };
      });
    }

    if (status.toLowerCase() == 'update') {
      const body = req.vals;

      if (obegpid <= 0) {
        obegpid = await this.db.obegp.add({
          OBEGEID: obegeid,
          BegehDatum: body.inspektiondate,
          Completed: req.completed,
          Bemerkung: body.bemerkung ?? '',
          ...GetDBStatus(),
        });
      } else {
        listItems.push(obegeid);
        await this.db.obegp.update(obegeid, {
          OBEGEID: obegeid,
          BegehDatum: body.inspektiondate,
          Completed: req.completed,
          Bemerkung: body.bemerkung ?? '',
          ...GetDBStatus(),
        });
      }

      const result = {};
      result['obegpid'] = obegpid;
      return { success: true, result: result };
    }

    return { success: true };
  }

  public async getBegehungsplan(): Promise<MRproResponseInfo<BegehungResult>> {
    try {
      const sxbeg = await this.db.sxbeg.toArray();
      const objekte = (await this.db.ostamm.toArray()).map(x => x.OSTAMMID);

      const rows = await this.db.obege.orderBy('Titel').toArray();

      const groupIDs = await this.sharedDB.GetGroupIDs();
      const filtered_rows = rows.filter(row => {
        const zuordnungen = sxbeg.filter(x => x.OBEGEID == row.ID);
        const isAllInRep = zuordnungen.every(x => objekte.includes(x.OSTAMMID));

        return isAllInRep && row.Gruppen.split(';').some(groupID => groupIDs.includes(+groupID));
      });

      const mapped_rows = AsMappedRows<BegehungPlanNode>()(filtered_rows, [
        ['ID', 'OBEGEID'],
        ['LPOBEGPID', 'OBEGPID'],
        ['Titel', 'Begehungsart'],
        ['LPDatum', 'letzte'],
        ['LPCompleted', 'abgeschlossen'],
        ['BeginnDatum', 'Intervallbeginn'],
        'Intervall',
        ['Tag', '_tag'],
        ['Bemerkung', 'Beschreibung'],
        'UserName',
        'Status',
        'Gruppen',
      ]);

      for (const row of mapped_rows) {
        if (row.letzte == null) row.OBEGPID = 0;
        let oDate = dayjs(row.Intervallbeginn);
        const lastDate = dayjs(row.letzte);
        while (lastDate.diff(oDate, 'days') >= 0) {
          oDate = oDate.add(row.Intervall, 'days');
        }

        if (row._tag > oDate.day()) oDate = oDate.day(row._tag);
        else if (row._tag < oDate.day()) oDate = oDate.add(1, 'week').day(row._tag);

        row.Termin = oDate.format(BackendLocaleDatePipe.format);
        row.Wochentag = oDate.format('dddd');
      }
      const obege = AsTableFE(mapped_rows);

      const obegp_history = AsMappedTableFE<BegehungHistorieNode>()<OBEGP & OBEGE>(
        [],
        [
          'ID',
          ['Titel', 'Begehungsart'],
          'BegehDatum',
          ['Completed', 'Abgeschlossen'],
          'Bemerkung',
          'UserName',
          'Status',
        ]
      );

      return { success: true, obege, sxbeg, obegp_history };
    } catch (ex) {
      return { success: false, error: (ex as Error).message };
    }
  }

  public async getBegehungsplanHistory(OBEGEID: number): Promise<MRproResponseInfo<BegehungResult>> {
    return await this.zone.runOutsideAngular(async () => {
      try {
        const obegp = await this.db.obegp.where({ OBEGEID }).toArray();
        const obegp_rows = await Promise.all(
          obegp.map(async row => {
            const obege = await this.db.obege.get(row.OBEGEID);
            if (!obege) return null;
            return { ...row, Begehungsart: obege.Titel };
          })
        );

        const obegp_history = AsMappedTableFE<BegehungHistorieNode>()(obegp_rows, [
          'ID',
          'Begehungsart',
          'BegehDatum',
          ['Completed', 'Abgeschlossen'],
          'Bemerkung',
          'UserName',
          'Status',
        ]);

        return { success: true, obegp_history };
      } catch (ex) {
        return { success: false, error: (ex as Error).message };
      }
    });
  }

  private async GetMangelItems(OPRUEFID: number): Promise<string> {
    const compoundKeys = await this.db.omazs
      .where('[OPRUEFID+OMAZSID]')
      .between([OPRUEFID, Dexie.minKey], [OPRUEFID, Dexie.maxKey])
      .keys();

    return compoundKeys.map(k => k[1]).join(';');
  }
  //#endregion
}
